/**
 * C7 — the agency's brand on its portal host's sign-in page (white label:
 * no SynAnalytics name or logo), and the page for a portal address that is
 * not set up.
 */

import { createContext, useContext, type ReactNode } from 'react';

import type { PortalBrand } from './portal-host.ts';

export const PortalBrandContext = createContext<PortalBrand | null>(null);

export function usePortalBrand(): PortalBrand | null {
  return useContext(PortalBrandContext);
}

const SAFE_COLOUR = /^#[0-9a-fA-F]{6}$/;

/** The agency's own sign-in frame: its logo or name, its colour on the buttons. */
export function PortalLoginFrame({
  brand,
  children,
}: {
  brand: PortalBrand;
  children: ReactNode;
}): ReactNode {
  const accent = brand.accent && SAFE_COLOUR.test(brand.accent) ? brand.accent : null;
  return (
    <main
      className="li li-portal"
      style={
        accent
          ? ({ '--accent-strong': accent, '--accent': accent } as React.CSSProperties)
          : undefined
      }
    >
      <div className="li-portal-card">
        <header className="li-portal-brand">
          {brand.logo ? <img src={brand.logo} alt={brand.name} /> : <strong>{brand.name}</strong>}
        </header>
        {children}
      </div>
    </main>
  );
}

export function PortalNotFound({ host }: { host: string }): ReactNode {
  return (
    <main className="li-callback">
      <h1>This portal is not set up</h1>
      <p>
        {host} is not a client portal yet. If you expected one here, ask the agency that sent you
        the link.
      </p>
    </main>
  );
}
